import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  TableDownstreamWorkbooksFilteredByColumnQuery,
  TableDownstreamWorkbooksFilteredByColumnQuery_tables_columns_downstreamWorkbooksConnection_nodes,
  TableDownstreamWorkbooksFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableDownstreamWorkbooksFilteredByColumnQuery';
import {
  TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQuery,
  TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQuery_tables_columns_downstreamWorkbooksConnection_nodes,
  TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQuery';
import {
  TableDownstreamWorkbooksQuery,
  TableDownstreamWorkbooksQuery_tables_downstreamWorkbooksConnection_nodes,
  TableDownstreamWorkbooksQueryVariables,
} from '../../../gen-src/queries/TableDownstreamWorkbooksQuery';
import {
  TableDownstreamWorkbooksWithoutPersonalSpaceQuery,
  TableDownstreamWorkbooksWithoutPersonalSpaceQuery_tables_downstreamWorkbooksConnection_nodes,
  TableDownstreamWorkbooksWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/TableDownstreamWorkbooksWithoutPersonalSpaceQuery';
import { WorkbookSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableDownstreamWorkbooksFilteredByColumnQueryGql } from '../../queries/TableDownstreamWorkbooksFilteredByColumnQueryGql';
import { TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQueryGql';
import { TableDownstreamWorkbooksQueryGql } from '../../queries/TableDownstreamWorkbooksQueryGql';
import { TableDownstreamWorkbooksWithoutPersonalSpaceQueryGql } from '../../queries/TableDownstreamWorkbooksWithoutPersonalSpaceQueryGql';
import {
  getRowDataForWorkbook,
  getRowDataForWorkbookWithoutPersonalSpace,
  WorkbooksDisplayGrid,
  WorkbooksDisplayGridRowData
} from '../sharedDisplayGrid/WorkbooksDisplayGrid';
import { WorkbooksValidSortFields } from '../sorting/sortProviders/WorkbooksGridSortProvider';

type QueryVariables = TableDownstreamWorkbooksQueryVariables;
type QueryResults = TableDownstreamWorkbooksQuery;
type QueryResultListNode = TableDownstreamWorkbooksQuery_tables_downstreamWorkbooksConnection_nodes;
type GridRowData = WorkbooksDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = TableDownstreamWorkbooksWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = TableDownstreamWorkbooksWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = TableDownstreamWorkbooksWithoutPersonalSpaceQuery_tables_downstreamWorkbooksConnection_nodes;

type QueryVariablesWithFilter = TableDownstreamWorkbooksFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamWorkbooksFilteredByColumnQuery;
type QueryResultListNodeWithFilter =
  TableDownstreamWorkbooksFilteredByColumnQuery_tables_columns_downstreamWorkbooksConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQuery;
type QueryResultListNodeWithFilterWithoutPersonalSpace =
  TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQuery_tables_columns_downstreamWorkbooksConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  return resultData && resultData.tables && resultData.tables[0] && resultData.tables[0].columns &&
    resultData.tables[0].columns[0].downstreamWorkbooksConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] && resultData.tables[0].columns &&
    resultData.tables[0].columns[0].downstreamWorkbooksConnection;
}

export type TableDownstreamWorkbooksQueryGridProps =
  GridSortProps<WorkbooksValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableDownstreamWorkbooksQueryGrid(props: TableDownstreamWorkbooksQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyDownstreamWorkbooksLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredWorkbooksLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
        queryGql={TableDownstreamWorkbooksFilteredByColumnQueryGql}
        partialQueryVariables={{ tableId: props.tableId, columnId: props.filteredColumnId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForWorkbook}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={TableDownstreamWorkbooksQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForWorkbook}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredColumnId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithFilterWithoutPersonalSpace, GridRowData>
        queryGql={TableDownstreamWorkbooksFilteredByColumnWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.tableId, columnId: props.filteredColumnId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={TableDownstreamWorkbooksWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as WorkbookSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForWorkbookWithoutPersonalSpace}
      >
        {({ rowStore }) => <WorkbooksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    )
  );
}
